import { generateUUID } from './uuid.js';

const tagId = tag => typeof tag === 'string' ? tag : tag?.tagId;
const tagName = tag => typeof tag?.name === 'string' ? tag.name.trim().toLowerCase() : null;

/**
 * Copy a subtree's referenced tag definitions into another document's namespace.
 * Used with an empty destination when copying, and the current destination when
 * pasting. A per-node source lookup also supports nodes from linked documents.
 * Existing destination tags win by name; their IDs and settings never change.
 * Neither input is mutated. Legacy string references and setAt are preserved.
 */
export function transferClipboardTags(root, getSourceDefinitions, destinationDefinitions = []) {
  const definitions = [...destinationDefinitions];
  const byId = new Map(definitions.map(tag => [tag.id, tag]));
  const byName = new Map();
  for (const tag of definitions) {
    const name = tagName(tag);
    if (name && !byName.has(name)) byName.set(name, tag);
  }

  function copy(node) {
    const result = { ...node };
    if (Array.isArray(node.tags) && node.tags.length) {
      const source = new Map((getSourceDefinitions(node.id) || [])
        .filter(tag => typeof tag?.id === 'string' && tagName(tag))
        .map(tag => [tag.id, tag]));
      result.tags = node.tags.map(reference => {
        const id = tagId(reference);
        const definition = source.get(id);
        // Older clipboard payloads have IDs but no definitions. Keep their
        // references intact so same-document paste remains compatible.
        if (!definition) return typeof reference === 'object' && reference
          ? { ...reference } : reference;

        const name = tagName(definition);
        const sameId = byId.get(id);
        let target = sameId && tagName(sameId) === name ? sameId : byName.get(name);
        if (!target) {
          let newId = id;
          while (byId.has(newId)) newId = generateUUID();
          target = { id: newId, name: definition.name };
          if (typeof definition.color === 'string') target.color = definition.color;
          if (definition.mandatory === true) target.mandatory = true;
          definitions.push(target);
          byId.set(newId, target);
          byName.set(name, target);
        }
        return typeof reference === 'string' ? target.id : { ...reference, tagId: target.id };
      });
    }
    if (Array.isArray(node.children)) result.children = node.children.map(copy);
    return result;
  }

  const node = copy(root);
  return { node, tagDefinitions: definitions };
}
